<template>
  <div v-if="visibleBanners.length" class="moo-menu-banner">
    <swiper
      v-if="visibleBanners.length > 1"
      :slides-per-view="1"
      :space-between="0"
      :loop="true"
      :pagination="{ clickable: true }"
      :autoplay="{ delay: 4000, disableOnInteraction: false }"
      @click="onSwiperClick"
    >
      <swiper-slide v-for="(banner, index) in visibleBanners" :key="banner.id || index">
        <div
          class="moo-menu-banner__slide"
          :class="{ 'moo-menu-banner__slide--tappable': isTappable(banner) }"
        >
          <img
            class="moo-menu-banner__image"
            :src="banner.file_url"
            :alt="banner.item_name || 'Promotion banner'"
            loading="lazy"
            @error="onImgError"
          />
          <div v-if="banner.item_name" class="moo-menu-banner__caption">
            <div class="moo-menu-banner__caption-overlay" aria-hidden="true"></div>
            <div class="moo-menu-banner__caption-name">{{ tName(banner.item_name) }}</div>
            <div v-if="priceOf(banner)" class="moo-menu-banner__caption-price">
              {{ priceOf(banner) }}
            </div>
          </div>
        </div>
      </swiper-slide>
    </swiper>

    <div
      v-else
      class="moo-menu-banner__slide"
      :class="{ 'moo-menu-banner__slide--tappable': isTappable(visibleBanners[0]) }"
      @click="onBannerTap(visibleBanners[0])"
    >
      <img
        class="moo-menu-banner__image"
        :src="visibleBanners[0].file_url"
        :alt="visibleBanners[0].item_name || 'Promotion banner'"
        loading="lazy"
        @error="onImgError"
      />
      <div v-if="visibleBanners[0].item_name" class="moo-menu-banner__caption">
        <div class="moo-menu-banner__caption-overlay" aria-hidden="true"></div>
        <div class="moo-menu-banner__caption-name">
          {{ tName(visibleBanners[0].item_name) }}
        </div>
        <div v-if="priceOf(visibleBanners[0])" class="moo-menu-banner__caption-price">
          {{ priceOf(visibleBanners[0]) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from "swiper/vue";
import { translatedName } from "@/i18n/menuTranslate";
import SwiperCore, { Pagination, Autoplay } from "swiper";
import "swiper/swiper-bundle.min.css";
import "swiper/swiper.min.css";

SwiperCore.use([Pagination, Autoplay]);

// Self-contained SVG placeholder shown when an upsell item's image fails to
// load (there is no reliable noimage asset in this project).
const BANNER_PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='180' viewBox='0 0 400 180'%3E%3Crect width='400' height='180' fill='%23f0eeee'/%3E%3Cg fill='none' stroke='%23c9c4c4' stroke-width='6'%3E%3Crect x='150' y='55' width='100' height='70' rx='8'/%3E%3Ccircle cx='176' cy='80' r='9'/%3E%3Cpath d='M158 118l28-26 20 18 16-14 22 22'/%3E%3C/g%3E%3C/svg%3E";

export default {
  name: "MenuBanner",
  components: { Swiper, SwiperSlide },
  props: {
    banners: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["tap"],
  computed: {
    visibleBanners() {
      return (this.banners || []).filter((b) => b && b.file_url);
    },
  },
  methods: {
    // MTH047: banner captions carry POS item names, so they must go through the
    // menu-translation map like the rest of the menu. `translatedName` reads a
    // reactive store, so the caption re-renders when the top bar switches
    // language (and returns the source name on English / while it loads).
    tName(source) {
      return translatedName(source);
    },
    // Render the upsell item's price in the caption. Banner rows are already
    // unwrapped to the menu item in Order.vue's fetchUpsellBanners (which drops
    // $0 items), so price_default is positive for item banners.
    priceOf(banner) {
      if (!banner) return "";
      const price =
        parseFloat(banner.price_default) || parseFloat(banner.member_price) || 0;
      return price > 0 ? `$ ${price.toFixed(2)}` : "";
    },
    isTappable(banner) {
      if (!banner) return false;
      return !!(banner.item_name || banner.id);
    },
    onSwiperClick(swiper) {
      // Swiper `loop` mode clones slides; cloned DOM nodes are created outside
      // Vue, so a per-slide @click never fires when the visible slide is a
      // clone. Handle the click at the Swiper level and resolve the real slide
      // via realIndex (slides-per-view is 1 so the active slide is the tapped
      // one).
      if (!swiper) return;
      this.onBannerTap(this.visibleBanners[swiper.realIndex]);
    },
    onBannerTap(banner) {
      if (!banner) return;
      this.$emit("tap", banner);
    },
    onImgError(event) {
      // Swap a broken upsell image for the placeholder (guard against a loop if
      // the placeholder itself somehow fails).
      if (event?.target && event.target.src !== BANNER_PLACEHOLDER) {
        event.target.src = BANNER_PLACEHOLDER;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.moo-menu-banner {
  margin: 0 0 1rem;
}

// Swiper's pagination container is absolutely positioned with z-index over the
// slide. Let taps pass through the container to the slide while keeping the
// individual bullets clickable.
.moo-menu-banner :deep(.swiper-pagination) {
  pointer-events: none;
}

.moo-menu-banner :deep(.swiper-pagination-bullet) {
  pointer-events: auto;
}

.moo-menu-banner__slide {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 16px;
  overflow: hidden;
  text-align: left;
  color: inherit;

  &--tappable {
    cursor: pointer;

    &:active {
      transform: scale(0.99);
    }
  }
}

.moo-menu-banner__image {
  display: block;
  width: 100%;
  height: auto;
  max-height: 180px;
  object-fit: cover;
}

.moo-menu-banner__caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0.5rem 0.85rem;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
}

.moo-menu-banner__caption-overlay {
  position: absolute;
  inset: 0;
  background: #000;
  opacity: 0.3;
}

.moo-menu-banner__caption-name {
  position: relative;
  flex: 1;
  min-width: 0;
  color: #fff;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.moo-menu-banner__caption-price {
  position: relative;
  flex-shrink: 0;
  color: #fff;
  font-weight: 700;
  font-size: 0.875rem;
  line-height: 1.2;
}
</style>
